import { useCallback, useRef, useState, type MouseEvent } from 'react';

interface UseOrderBulkSelectionOptions {
    orderIds: string[];
}

/** Shift+click row/checkbox for range select; otherwise runs the default row action. */
export const handleOrderBulkRowClick = (
    event: MouseEvent,
    orderId: string,
    rowIndex: number,
    onToggle: (orderId: string, rowIndex: number, shiftKey: boolean) => void,
    onDefaultAction: () => void,
    bulkEnabled: boolean,
) => {
    if (bulkEnabled && event.shiftKey) {
        event.preventDefault();
        onToggle(orderId, rowIndex, true);
        return;
    }
    onDefaultAction();
};

export const useOrderBulkSelection = ({ orderIds }: UseOrderBulkSelectionOptions) => {
    const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(() => new Set());
    const lastClickedIndexRef = useRef<number | null>(null);

    const toggleOrderSelected = useCallback((orderId: string, rowIndex: number, shiftKey: boolean) => {
        setSelectedOrderIds((prev) => {
            const next = new Set(prev);

            if (shiftKey && lastClickedIndexRef.current !== null) {
                const start = Math.min(lastClickedIndexRef.current, rowIndex);
                const end = Math.max(lastClickedIndexRef.current, rowIndex);
                for (let i = start; i <= end; i++) {
                    const id = orderIds[i];
                    if (id) next.add(id);
                }
            } else if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }

            return next;
        });
        lastClickedIndexRef.current = rowIndex;
    }, [orderIds]);

    const allPageSelected =
        orderIds.length > 0 && orderIds.every((id) => selectedOrderIds.has(id));
    const somePageSelected = orderIds.some((id) => selectedOrderIds.has(id));
    const isPageSelectionIndeterminate = somePageSelected && !allPageSelected;

    const toggleSelectAllOnPage = useCallback(() => {
        setSelectedOrderIds((prev) => {
            const next = new Set(prev);
            const allSelected = orderIds.length > 0 && orderIds.every((id) => next.has(id));
            if (allSelected) {
                orderIds.forEach((id) => next.delete(id));
            } else {
                orderIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }, [orderIds]);

    const deselectOrder = useCallback((orderId: string) => {
        setSelectedOrderIds((prev) => {
            if (!prev.has(orderId)) return prev;
            const next = new Set(prev);
            next.delete(orderId);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedOrderIds(new Set());
        lastClickedIndexRef.current = null;
    }, []);

    return {
        selectedOrderIds,
        toggleOrderSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        deselectOrder,
        clearSelection,
    } as const;
};
